import { Asset } from '@/modules/asset/models/Asset';
import { Billing } from '@/modules/billing/models/Billing';
import { BillingSubscriptionHistory } from '@/modules/billing/models/BillingSubscriptionHistory';
import { Company } from '@/modules/company/models/Company';
import { DateFormat } from '@/modules/core/app/constants/date.constants';
import { Courthouse } from '@/modules/courthouse/models/Courthouse';
import { DocumentCollection } from '@/modules/document/models/DocumentCollection';
import { EsignEnvelopeCollection } from '@/modules/esign/models/EsignEnvelopeCollection';
import { EstateStatus } from '@/modules/estate/constants/EstateStatus';
import { EstateStatusKey } from '@/modules/estate/constants/EstateStatusKey';
import { EstateType } from '@/modules/estate/enums/EstateType';
import { EstateConsent } from '@/modules/estate/models/EstateConsent';
import { EstateForm } from '@/modules/estate/models/EstateForm';
import { EstateFormType } from '@/modules/estate/models/EstateFormType';
import { EstateHubspotDeal } from '@/modules/estate/models/EstateHubspotDeal';
import { EstateNotifyCreditorEntry } from '@/modules/estate/models/EstateNotifyCreditorEntry';
import { EstateProduct } from '@/modules/estate/models/EstateProduct';
import { EstateQuebecSpouseClaim } from '@/modules/estate/models/EstateQuebecSpouseClaim';
import { EstateAccount } from '@/modules/estateaccount/models/EstateAccount';
import { EstateInterimDistribution } from '@/modules/estateInterimDistribution/models/EstateInterimDistribution';
import { EstatePlanningType } from '@/modules/estateplanningtype/models/EstatePlanningType';
import { EstateTask } from '@/modules/estatetask/models/EstateTask';
import { EstateUpsell } from '@/modules/estateupsell/models/EstateUpsell';
import { EstateUpsellClearWillComponentProp } from '@/modules/estateupsell/constants/EstateUpsellClearWillComponentProp';
import { HubSpotContact } from '@/modules/hubspot/models/HubSpotContact';
import { HubspotMeetingNoteCollection } from '@/modules/hubspot/models/HubspotMeetingNoteCollection';
import { NoteCollection } from '@/modules/note/models/NoteCollection';
import { Opportunity } from '@/modules/opportunity/models/Opportunity';
import { PredefinedCountry } from '@/modules/predefined/enums/PredefinedCountry';
import { Jurisdiction } from '@/modules/predefined/models/Jurisdiction';
import { StatusEntryCollection } from '@/modules/statusentry/models/StatusEntryCollection';
import { TimeEntryCollection } from '@/modules/timeentry/models/TimeEntryCollection';
import { User } from '@/modules/user/models/User';
import { WillAdditionalNeedQuebecType } from '@/modules/will/constants/WillAdditionalNeedQuebecType';
import { WillAdditionalNeedType } from '@/modules/will/constants/WillAdditionalNeedType';
import { camelCase } from 'lodash';
import dayjs from 'dayjs';
import { ColumnFormat } from '/@/modules/core/app/constants/data.constants';
import { Locale } from '/@/modules/core/app/constants/Locale';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import Lang from '/@/modules/core/app/services/Lang';
import NumeralService from '/@/modules/core/app/services/NumeralService';
import { DocumentSubType } from '/@/modules/document/enums/DocumentSubType';
import { EstateDocumentSubType } from '/@/modules/document/enums/EstateDocumentSubType';
import { VaultDocumentSubType } from '/@/modules/document/enums/VaultDocumentSubType';
import { GuidanceStepStage } from '/@/modules/guidancestep/enums/GuidanceStepStage';
import { PredefinedJurisdiction } from '/@/modules/predefined/enums/PredefinedJurisdiction';
// for some reason Contact needs to be imported last, otherwise we get
// "cannot access Contact before initialization"
import { Hubspot } from '@/integrations/hubspot/hubspot.constants';
import { AuthUser } from '@/modules/auth/models/AuthUser';
import { Contact } from '@/modules/contact/models/Contact';
import { DisclaimerType } from '@/modules/disclaimer/constants/DisclaimerType';
import { Disclaimer } from '@/modules/disclaimer/models/Disclaimer';
import { EstatePaymentStatus } from '@/modules/estate/constants/EstatePaymentStatus';
import { EstateSettingType } from '@/modules/estate/enums/EstateSettingType';
import { EstateSetting } from '@/modules/estate/models/EstateSetting';
import { EstateHealthCheck } from '@/modules/estatehealthcheck/models/EstateHealthCheck';
import { EstateTaxFiling } from '@/modules/estatetaxfiling/models/EstateTaxFiling';
import { Liability } from '@/modules/liability/models/Liability';
import { LightDiagnosticSubmission } from '@/modules/lightdiagnostic/models/LightDiagnosticSubmission';
import { MeetingCollection } from '@/modules/meeting/models/MeetingCollection';
import { Audit } from '@/modules/audit/models/Audit';
import { Referral } from '@/modules/referral/models/Referral';
import { PredefinedEstateRole } from '@/modules/role/enums/PredefinedEstateRole';
import { LocaleType } from '@/modules/user/constants/LocaleType';
import { Will } from '@/modules/will/models/Will';
import { WillsNoticeSearch } from '@/modules/will/models/WillsNoticeSearch';
import { getters } from '/@/modules/core/app/helpers/store';
import { BillingPlanMapping } from '@/modules/billing/models/BillingPlanMapping';

export class Estate extends BaseEntityModel {
  id?: number;

  vaultId?: string;

  type: string;

  jurisdictionId: number;

  jurisdiction: Relationship<Jurisdiction>;

  createdByUserId?: number;

  guidanceProgress: number;

  guidanceStage?: string;

  status?: string;

  reasonForClosure?: string;

  isDemo: boolean;

  hasHubspotDeal?: boolean;

  hasWill?: boolean;

  hasLocatedWill?: boolean;

  hasCodicilToWill?: boolean;

  hasDoneWillSearch?: boolean;

  needsProbate?: boolean;

  isProbateLocked: boolean;

  isLocked?: boolean;

  soleOwnerOfRealEstate?: boolean;

  ownAssetsAbove25k?: boolean;

  valueRange?: string;

  hasChildren?: boolean;

  isBondNeeded?: boolean;

  partnerEstateId?: number;

  isPrimarySpouse: boolean;

  contact: Relationship<Contact>;

  probateValue?: number;

  probateTax?: number;

  closedAt?: string;

  teamUserId?: number;

  notaryAssignedAt?: string;

  users?: Relationship<User[]> | null;

  hubspotContacts?: Relationship<HubSpotContact[]> | null;

  assets?: Relationship<Asset[]> | null;

  liabilities?: Relationship<Liability[]> | null;

  estateAccount?: Relationship<EstateAccount> | null;

  estateAccounts?: Relationship<EstateAccount[]> | null;

  documents: Relationship<DocumentCollection>;

  notes: Relationship<NoteCollection>;

  estateNotes: Relationship<NoteCollection>;

  company?: Relationship<Company> | null;

  createdByUser?: Relationship<User> | null;

  estateForms?: Relationship<EstateForm[]> | null;

  partnerEstate?: Relationship<Estate> | null;

  accountantTask?: Relationship<EstateTask> | null;

  lawyerTask?: Relationship<EstateTask> | null;

  notaryTask?: Relationship<EstateTask> | null;

  estateInterimDistribution?: Relationship<EstateInterimDistribution> | null;

  estateNotifyCreditorEntry?: Relationship<EstateNotifyCreditorEntry> | null;

  billing?: Relationship<Billing> | null;

  disclaimers?: Relationship<Disclaimer[]> | null;

  estateUpsell?: Relationship<EstateUpsell> | null;

  estateConsents?: Relationship<EstateConsent[]> | null;

  estateHubspotDeal?: Relationship<EstateHubspotDeal> | null;

  estateQuebecSpouseClaim?: Relationship<EstateQuebecSpouseClaim> | null;

  willTask?: Relationship<EstateTask> | null;

  userTasks?: Relationship<EstateTask[]> | null;

  estatePlanningTypes?: Relationship<EstatePlanningType[]> | null;

  hubspotMeetingNotes: Relationship<HubspotMeetingNoteCollection>;

  timeEntries: Relationship<TimeEntryCollection>;

  statusEntries: Relationship<StatusEntryCollection>;

  courthouse?: Relationship<Courthouse> | null;

  esignEnvelopes: Relationship<EsignEnvelopeCollection>;

  willsNoticeSearch: Relationship<WillsNoticeSearch> | null;

  estateProducts: Relationship<EstateProduct[]> | null;

  meetings: Relationship<MeetingCollection>;

  estateTaxFilings?: Relationship<EstateTaxFiling[]> | null;

  will?: Relationship<Will> | null;

  lightDiagnosticSubmission?: Relationship<LightDiagnosticSubmission> | null;

  estateSettings?: Relationship<EstateSetting[]> | null;

  referral?: Relationship<Referral> | null;

  estateHealthChecks?: Relationship<EstateHealthCheck[]> | null;

  opportunities?: Relationship<Opportunity[]> | null;

  notaryStatus?: string;

  lastNotaryStatusTransitionAudit?: Audit | null;

  lawyerStatus?: string;

  lastLawyerStatusTransitionAudit?: Audit | null;

  constructor(model: Partial<Estate> = {}) {
    super(model);

    this.id = model.id;
    this.vaultId = model.vaultId;
    this.type = model.type as string;
    this.jurisdictionId = model.jurisdictionId as number;
    this.createdByUserId = model.createdByUserId;
    this.guidanceProgress = model.guidanceProgress ?? 0;
    this.guidanceStage = model.guidanceStage;
    this.status = model.status;
    this.reasonForClosure = model.reasonForClosure;
    this.isDemo = model.isDemo ?? false;
    this.hasHubspotDeal = model.hasHubspotDeal;
    this.hasWill = model.hasWill;
    this.isBondNeeded = model.isBondNeeded;
    this.hasLocatedWill = model.hasLocatedWill;
    this.hasCodicilToWill = model.hasCodicilToWill;
    this.hasDoneWillSearch = model.hasDoneWillSearch;
    this.soleOwnerOfRealEstate = model.soleOwnerOfRealEstate;
    this.ownAssetsAbove25k = model.ownAssetsAbove25k;
    this.valueRange = model.valueRange;
    this.hasChildren = model.hasChildren;
    this.probateValue = model.probateValue;
    this.probateTax = model.probateTax;
    this.partnerEstateId = model.partnerEstateId;
    this.isPrimarySpouse = model.isPrimarySpouse ?? false;
    this.needsProbate = model.needsProbate;
    this.isProbateLocked = model.isProbateLocked ?? false;
    this.isLocked = model.isLocked;
    this.closedAt = model.closedAt;
    this.teamUserId = model.teamUserId;
    this.notaryStatus = model.notaryStatus;
    this.lawyerStatus = model.lawyerStatus;
    this.notaryAssignedAt = model.notaryAssignedAt;
    this.contact = Contact.mapModel(model.contact);
    // @ts-ignore
    this.assets = Asset.mapModelsOrNull(model.assets ?? undefined);
    // @ts-ignore
    this.liabilities = Liability.mapModelsOrNull(model.liabilities ?? undefined);
    this.jurisdiction = Jurisdiction.mapModel(model.jurisdiction);
    this.company = Company.mapModelOrNull(model.company || undefined);
    this.courthouse = Courthouse.mapModelOrNull(model.courthouse || undefined);
    this.documents = DocumentCollection.mapModel(model.documents);
    // @ts-ignore
    this.estateAccounts = EstateAccount.mapModels(model.estateAccounts || undefined);
    this.users = User.mapModelsOrNull(model.users || undefined);
    this.notes = NoteCollection.mapModel(model.notes);
    this.estateNotes = NoteCollection.mapModel(model.estateNotes);
    this.createdByUser = User.mapModelOrNull(model.createdByUser || undefined);
    this.estateForms = EstateForm.mapModelsOrNull(model.estateForms || undefined);
    this.accountantTask = EstateTask.mapModelOrNull(model.accountantTask || undefined);
    this.lawyerTask = EstateTask.mapModelOrNull(model.lawyerTask || undefined);
    this.notaryTask = EstateTask.mapModelOrNull(model.notaryTask || undefined);
    this.billing = Billing.mapModelOrNull(model.billing || undefined);
    this.estateUpsell = EstateUpsell.mapModelOrNull(model.estateUpsell || undefined);
    this.estateConsents = EstateConsent.mapModelsOrNull(model.estateConsents || undefined);
    this.disclaimers = Disclaimer.mapModelsOrNull(model.disclaimers || undefined);
    this.estateHubspotDeal = EstateHubspotDeal.mapModelOrNull(model.estateHubspotDeal || undefined);
    this.estatePlanningTypes = EstatePlanningType.mapModelsOrNull(
      model.estatePlanningTypes || undefined
    );
    this.willTask = EstateTask.mapModelOrNull(model.willTask || undefined);
    this.estateQuebecSpouseClaim = EstateQuebecSpouseClaim.mapModelOrNull(
      model.estateQuebecSpouseClaim || undefined
    );
    this.userTasks = EstateTask.mapModelsOrNull(model.userTasks || undefined);
    this.hubspotMeetingNotes = HubspotMeetingNoteCollection.mapModel(
      model.hubspotMeetingNotes || undefined
    );
    this.timeEntries = TimeEntryCollection.mapModel(model.timeEntries);
    this.statusEntries = StatusEntryCollection.mapModel(model.statusEntries || undefined);
    this.partnerEstate = Estate.mapModelOrNull(model.partnerEstate || undefined);
    this.esignEnvelopes = EsignEnvelopeCollection.mapModel(model.esignEnvelopes);
    this.willsNoticeSearch = WillsNoticeSearch.mapModelOrNull(model.willsNoticeSearch);
    this.estateProducts = EstateProduct.mapModelsOrNull(model.estateProducts);
    this.meetings = MeetingCollection.mapModel(model.meetings);
    this.estateTaxFilings = EstateTaxFiling.mapModelsOrNull(model.estateTaxFilings);
    this.will = Will.mapModelOrNull(model.will);
    this.lightDiagnosticSubmission = LightDiagnosticSubmission.mapModelOrNull(
      model.lightDiagnosticSubmission
    );
    this.estateSettings = EstateSetting.mapModelsOrNull(model.estateSettings);
    this.referral = Referral.mapModelOrNull(model.referral);
    this.estateHealthChecks = EstateHealthCheck.mapModelsOrNull(model.estateHealthChecks);
    this.opportunities = Opportunity.mapModelsOrNull(model.opportunities);
    this.lastNotaryStatusTransitionAudit =
      model.lastNotaryStatusTransitionAudit !== undefined
        ? Audit.mapModelOrNull(model.lastNotaryStatusTransitionAudit ?? undefined)
        : undefined;
    this.lastLawyerStatusTransitionAudit =
      model.lastLawyerStatusTransitionAudit !== undefined
        ? Audit.mapModelOrNull(model.lastLawyerStatusTransitionAudit ?? undefined)
        : undefined;
  }

  getModelName(): ModelType {
    return ModelType.ESTATE;
  }

  get notaryTimeInStatus(): string | undefined {
    if (this.lastNotaryStatusTransitionAudit === undefined) {
      return undefined;
    }

    const startedAt: string | undefined =
      this.lastNotaryStatusTransitionAudit?.createdAt ?? this.createdAt;
    if (!startedAt) {
      return undefined;
    }

    const dayCount: number = dayjs().diff(dayjs(startedAt), 'day');

    if (dayCount < 1) {
      return Lang.t('notary.time-in-status-less-than-one-day');
    }

    return Lang.t('notary.time-in-status-days-counted', { count: dayCount }, dayCount);
  }

  get lawyerTimeInStatus(): string | undefined {
    if (this.lastLawyerStatusTransitionAudit === undefined) {
      return undefined;
    }

    const startedAt: string | undefined = this.lastLawyerStatusTransitionAudit?.createdAt;
    if (!startedAt) {
      return undefined;
    }

    const dayCount: number = dayjs().diff(dayjs(startedAt), 'day');

    if (dayCount < 1) {
      return Lang.t('notary.time-in-status-less-than-one-day');
    }

    return Lang.t('notary.time-in-status-days-counted', { count: dayCount }, dayCount);
  }

  get country(): PredefinedCountry {
    return PredefinedJurisdiction.getByKey(this.jurisdictionId)?.country as PredefinedCountry;
  }

  get typeEnum(): EstateType {
    return EstateType.getByKey(this.type) as EstateType;
  }

  get currentEstateHealthCheck(): EstateHealthCheck | null | undefined {
    if (!this.estateHealthChecks?.length) {
      return null;
    }

    return [...this.estateHealthChecks].sort(
      (a: EstateHealthCheck, b: EstateHealthCheck) => (b.year ?? 0) - (a.year ?? 0)
    )[0];
  }

  get formattedSubscriptionRenewalDate(): string {
    const year: number | undefined = this.currentEstateHealthCheck?.year;
    const histories: BillingSubscriptionHistory[] =
      this.billing?.sortedBillingSubscriptionHistories ?? [];

    const matchingHistory: BillingSubscriptionHistory | undefined = histories.find(
      (history: BillingSubscriptionHistory) =>
        !!history.subscriptionEndsAt && dayjs(history.subscriptionEndsAt).year() === year
    );

    if (!matchingHistory?.subscriptionEndsAt) {
      return '';
    }

    return dayjs(matchingHistory.subscriptionEndsAt)
      .tz(dayjs.tz.guess())
      .format(DateFormat.FULL_MONTH_DAY_YEAR);
  }

  get statusEnum(): EstateStatus {
    // @ts-ignore
    return EstateStatus.getByKey(this.status);
  }

  get hoursSaved(): string {
    let multiplier: number = 1.5;
    if (this.isClearVault) {
      multiplier = 0.5;
    }

    const hours: number = Math.ceil(this.guidanceProgress * multiplier);
    return NumeralService.formatValue(hours, ColumnFormat.FORMAT_INTEGER);
  }

  get moneySaved(): string {
    let multiplier: number = 75;
    if (this.isClearVault) {
      multiplier = 37.5;
    }

    const money: number = Math.ceil(this.guidanceProgress * multiplier);
    return NumeralService.formatValue(money, ColumnFormat.FORMAT_CURRENCY);
  }

  get displayGuidanceStage(): string | undefined {
    const stage: GuidanceStepStage | undefined = GuidanceStepStage.getByKey(this.guidanceStage);
    return stage?.getValue?.() || stage?.value;
  }

  get isTestate(): boolean {
    if (this.isInQuebecJurisdiction) {
      return !!(this.hasWill && this.hasDoneWillSearch);
    }

    return !!(this.hasWill && this.hasLocatedWill);
  }

  get isIntestate(): boolean {
    if (this.isInQuebecJurisdiction) {
      return this.hasWill === false && this.hasDoneWillSearch === false;
    }
    return this.hasWill === false && this.hasLocatedWill === false;
  }

  get isClearVault(): boolean {
    return this.type === EstateType.CLEAR_VAULT.key;
  }

  get isClearEstate(): boolean {
    return this.type === EstateType.CLEAR_ESTATE.key;
  }

  get isInactive(): boolean {
    return this.status
      ? EstateStatus.getClosedStatusKeys().includes(this.status as EstateStatusKey)
      : false;
  }

  get shouldShowArTag(): boolean {
    if (!this.isClearVault) {
      return false;
    }
    return this.status
      ? EstateStatus.getArTagActiveStatusKeys().includes(this.status as EstateStatusKey)
      : false;
  }

  get isClearWill(): boolean {
    return this.type === EstateType.CLEAR_WILL.key;
  }

  get guidanceRequested(): string {
    if (!this.isClearWill) {
      return '';
    }

    return EstateUpsellClearWillComponentProp.getValuesByProp('key')
      .filter(
        (key: string) =>
          !!this.estateUpsell?.meta?.[camelCase(key) as keyof typeof this.estateUpsell.meta]
      )
      .join(', ');
  }

  get isShell(): boolean {
    return this.status === EstateStatus.REFERRAL_PRE_FILLING.key;
  }

  get isReferral(): boolean {
    return this.isShell;
  }

  get displayType(): string {
    return this.isClearEstate ? Lang.t('accounting.estate') : Lang.t('estate.plan');
  }

  get displayRelationLabel(): string {
    if (this.isSelfServePlan) {
      return Lang.t('estate.relation-to-you');
    }
    if (this.isClearEstate) {
      return Lang.t('beneficiary.relation-to-deceased');
    }
    if (this.isClearVault) {
      const authUser: AuthUser | null = getters.auth.getUser();

      if (authUser && authUser.id === this.contact.userId) {
        return Lang.t('estate.relation-to-you');
      }

      return Lang.t('estate.relation-to-name', { name: this.fullName });
    }

    return '';
  }

  get plan(): BillingPlanMapping | null | undefined {
    return this.isReferral ? this.referral?.billingPlanMapping : this.billing?.billingPlanMapping;
  }

  get isStandardPlan(): boolean {
    return this.billing?.isStandardPlan || this.isClearWill;
  }

  get isBasicPlan(): boolean {
    return !!this.billing?.isBasicPlan;
  }

  get isCouplesBasicPlan(): boolean {
    return !!this.billing?.isCouplesBasicPlan;
  }

  get isSelfServePlan(): boolean {
    return !!this.billing?.isSelfServePlan;
  }

  get isGuidedPlan(): boolean {
    return !!this.billing?.isGuidedPlan;
  }

  get isDigitalVaultPlan(): boolean {
    return !!this.billing?.isDigitalVaultPlan;
  }

  get isCouplesSelfServePlan(): boolean {
    return !!this.billing?.isCouplesSelfServePlan;
  }

  get isEstateRepresentativePlan(): boolean {
    return !!this.billing?.isEstateRepresentativePlan;
  }

  get isCompleteEstatePlan(): boolean {
    return !!this.billing?.isCompleteEstatePlan;
  }

  get isProbatePlan(): boolean {
    return !!this.billing?.isProbatePlan;
  }

  get isProbateAndTaxesPlan(): boolean {
    return !!this.billing?.isProbateAndTaxesPlan;
  }

  get fullName(): string {
    return this.contact.fullName ?? 'Jane Doe';
  }

  get fullNameWithTitle(): string {
    if (this.contact.title) {
      return `${this.contact.title} ${this.fullName}`;
    }

    return this.fullName;
  }

  get dateOfDeathFormatted(): string {
    return dayjs(this.contact.dateOfDeath).format(DateFormat.FULL_MONTH_DAY_YEAR);
  }

  get dateOfDeathFormattedAbbr(): string {
    return dayjs(this.contact.dateOfDeath).format(DateFormat.MONTH_DAY_YEAR);
  }

  get isCouplesPlan(): boolean {
    return !!this.partnerEstateId;
  }

  get probateDocumentsList(): string[] {
    if (this.isTestate) {
      return [
        Lang.t('estate.original-proof-of'),
        Lang.t('estate.original-will'),
        Lang.t('estate.affidavit-of-of'),
        Lang.t('estate.certified-check-probate')
      ];
    }
    return [Lang.t('estate.original-proof-of'), Lang.t('estate.certified-check-probate')];
  }

  get displayBillingPlan(): string {
    return this.billing?.billingPlanMapping?.planName ?? Lang.t('estate.clearestate-plan');
  }

  get planner(): EstateAccount | undefined {
    return this.estateAccounts?.find((estateAccount: EstateAccount) => estateAccount.isPlanner);
  }

  get executor(): EstateAccount | undefined {
    return this.estateAccounts?.find((estateAccount: EstateAccount) => estateAccount.isExecutor);
  }

  get hasMultipleExecutors(): boolean {
    if (!this.estateAccounts) {
      return false;
    }

    return (
      this.estateAccounts?.filter((estateAccount: EstateAccount) => estateAccount.isExecutor)
        .length > 1
    );
  }

  get partnerEstateAccount(): EstateAccount | undefined {
    return (
      this.estateAccounts?.find((estateAccount: EstateAccount) => estateAccount.isPartner) ||
      this.estateAccounts?.find(
        (estateAccount: EstateAccount) =>
          estateAccount.user?.contact?.estateId === this.partnerEstateId
      )
    );
  }

  get hasPartnerEstateAccount(): boolean {
    return !!this.partnerEstateAccount;
  }

  get estateProfessional(): EstateAccount | undefined {
    return this.estateAccounts?.find(
      (estateAccount: EstateAccount) =>
        estateAccount.isEstateProfessional && estateAccount.user.professional.isEstateProfessional
    );
  }

  get juniorEstateProfessional(): EstateAccount | undefined {
    return this.estateAccounts?.find(
      (estateAccount: EstateAccount) =>
        estateAccount.isEstateProfessional &&
        estateAccount.user.professional.isJuniorEstateProfessional
    );
  }

  /**
   * this can be extended for non-Canadian jurisdictions
   */
  createEstateForms(): void {
    this.estateForms = EstateForm.mapModels(
      EstateFormType.getForms(this.jurisdictionId).map((type: EstateFormType) => ({
        type: type.key as string,
        esignTemplateKey: type.esignTemplateKey as number
      }))
    );
  }

  get displayProductType(): string {
    return this.typeEnum.value;
  }

  get displayFilterName(): string {
    return this.typeEnum.filterName;
  }

  get availableDocumentSubType(): typeof DocumentSubType {
    switch (this.type) {
      case EstateType.CLEAR_ESTATE.key:
        return EstateDocumentSubType;
      case EstateType.CLEAR_VAULT.key:
        return VaultDocumentSubType;
      case EstateType.CLEAR_WILL.key:
        return VaultDocumentSubType;
      default:
        return DocumentSubType;
    }
  }

  get locale(): string {
    return PredefinedJurisdiction.isCanadianJurisdiction(this.jurisdictionId)
      ? Locale.ENGLISH_CANADA
      : Locale.ENGLISH_US;
  }

  get preferredLocale(): string {
    return this.planner?.user?.locale ?? LocaleType.ENGLISH;
  }

  get isDistributionFrameworkStageCompleted(): boolean {
    if (!this.guidanceStage) {
      return false;
    }

    switch (this.type) {
      case EstateType.CLEAR_ESTATE.key:
        return GuidanceStepStage.isStageLaterThanCurrentStage(
          GuidanceStepStage.CE_DISTRIBUTION_FRAMEWORK.key,
          this.guidanceStage
        );
      case EstateType.CLEAR_VAULT.key:
        return GuidanceStepStage.isStageLaterThanCurrentStage(
          GuidanceStepStage.CV_DISTRIBUTION_FRAMEWORK.key,
          this.guidanceStage
        );
      case EstateType.CLEAR_WILL.key:
        return GuidanceStepStage.isStageLaterThanCurrentStage(
          GuidanceStepStage.CW_DISTRIBUTION_FRAMEWORK.key,
          this.guidanceStage
        );
      default:
        return false;
    }
  }

  get selectableClosedStatuses(): EstateStatus[] {
    return this.isClearVault
      ? EstateStatus.getClosedPlanningStatuses()
      : EstateStatus.getClosedSettlementStatuses();
  }

  get selectableStatuses(): EstateStatus[] {
    if (this.isInactive) {
      return this.selectableClosedStatuses;
    }

    if (this.isClearEstate) {
      return EstateStatus.getActiveSettlementStatusesByCountry(this.country?.id) as EstateStatus[];
    }

    if (this.isClearVault) {
      return EstateStatus.getActivePlanningStatuses();
    }

    return [];
  }

  get hasEstateClosureCountdownStarted(): boolean {
    return !!(!this.isLocked && this.closedAt);
  }

  get formattedClosedAtDate(): string {
    return dayjs(this.closedAt).format(DateFormat.FULL_MONTH_DAY_YEAR);
  }

  get willAdditionalNeedTypeValues(): { key: string; value: string; description: string }[] {
    return this.jurisdictionId === PredefinedJurisdiction.QUEBEC.id
      ? WillAdditionalNeedQuebecType.getValues()
      : WillAdditionalNeedType.getValues();
  }

  get nextOfKinRoleId(): number {
    return this.isTestate ? PredefinedEstateRole.DEFAULT.id : PredefinedEstateRole.BENEFICIARY.id;
  }

  get isInAlbertaJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.ALBERTA.id;
  }

  get isInBritishColumbiaJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.BRITISH_COLUMBIA.id;
  }

  get isInManitobaJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.MANITOBA.id;
  }

  get isInNewBrunswickJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.NEW_BRUNSWICK.id;
  }

  get isInNewfoundlandAndLabradorJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.NEWFOUNDLAND_AND_LABRADOR.id;
  }

  get isInNorthwestTerritoriesJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.NORTHWEST_TERRITORIES.id;
  }

  get isInNovaScotiaJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.NOVA_SCOTIA.id;
  }

  get isInNunavutJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.NUNAVUT.id;
  }

  get isInOntarioJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.ONTARIO.id;
  }

  get isInQuebecJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.QUEBEC.id;
  }

  get isInSaskatchewanJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.SASKATCHEWAN.id;
  }

  get isInYukonJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.YUKON.id;
  }

  get isCaliforniaJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.CALIFORNIA.id;
  }

  get isTexasJurisdiction(): boolean {
    return this.jurisdictionId === PredefinedJurisdiction.TEXAS.id;
  }

  get isCommonLawLegalUnionJurisdiction(): boolean {
    return PredefinedJurisdiction.isCommonLawLegalUnionJurisdiction(this.jurisdictionId);
  }

  get isCanadianJurisdiction(): boolean {
    return PredefinedJurisdiction.isCanadianJurisdiction(this.jurisdictionId);
  }

  get hubspotDealUrl(): string {
    return this.hasHubspotDeal
      ? `${Hubspot.HUBSPOT_DEAL_BASE_URL}/${this.estateHubspotDeal?.dealId}`
      : '';
  }

  get hasSelfServeRegisteredDisclaimer(): boolean {
    return !!this.disclaimers?.some((disclaimer: Disclaimer) =>
      [
        DisclaimerType.FINALIZE_STANDARD_PLAN_REGISTRATION,
        DisclaimerType.FINALIZE_BASIC_PLAN_REGISTRATION
      ].includes(disclaimer.type as DisclaimerType)
    );
  }

  get hasWillReviewAcknowledgedDisclaimer(): boolean {
    return !!this.disclaimers?.some(
      (disclaimer: Disclaimer) => disclaimer.type === DisclaimerType.WILL_REVIEW_ACKNOWLEDGMENT
    );
  }

  get hasSelfServeMandatoryConsents(): boolean {
    if (!this.estateConsents?.length) {
      return false;
    }

    return !!this.estateConsents?.find((consent: EstateConsent) => {
      return consent.isConsentToPlanningPurchaseTerms && consent.value;
    });
  }

  get advisorSharingConsent(): EstateConsent | undefined {
    return this.estateConsents?.find(
      (consent: EstateConsent) => consent.isTypeConsentForPartnerToAccessEstateAccount
    );
  }

  get feeScheduleConsent(): EstateConsent | undefined {
    return this.estateConsents?.find(
      (consent: EstateConsent) => consent.isConsentToProfessionalExecutorFeeSchedule
    );
  }

  get paymentStatus(): EstatePaymentStatus {
    if (!this.isSelfServePlan) {
      return EstatePaymentStatus.PAID;
    }

    if (!this.estateProducts?.length) {
      return EstatePaymentStatus.UNPAID;
    }

    if (this.estateProducts?.some((product: EstateProduct) => product.isPaidSaaS)) {
      return EstatePaymentStatus.PAID_SAAS;
    }

    return EstatePaymentStatus.PAID;
  }

  get isSelfServe(): boolean {
    return this.isClearWill || this.isSelfServePlan;
  }

  get hasWillInputStagesLocked(): boolean {
    if (!this.estateSettings?.length) {
      return false;
    }

    return !!this.estateSettings.find(
      (setting: EstateSetting) =>
        setting.type === EstateSettingType.HAS_WILL_INPUT_STAGES_LOCKED && setting.value
    );
  }

  get jurisdictionName(): string {
    return PredefinedJurisdiction.getByKey(this.jurisdictionId, 'id')?.name ?? '';
  }
}
